import {
  TRACING_AI_ANALYSIS_TYPES,
  type TracingAiAnalysisType,
} from '../constants.js';
import { computeChunkInputHash, computeTransactionKey } from './hash.js';

/** Document as produced by the platform's `getOrderTransactions`. */
export interface TransactionDocumentInput {
  id?: string | null;
  type?: string | null;
  fileUrl?: string | null;
  fileContentType?: string | null;
  isMandatory?: boolean | null;
  /** Content version — changes when the document is re-uploaded/edited. */
  updatedAt?: Date | string | null;
  /** Stable content fingerprints of the underlying file (for reuse hashing). */
  contentMd5Hash?: string | null;
  fileHash?: string | null;
}

export interface TransactionEntityInput {
  id?: string | null;
  /** Company/asset id — stable across chains (unlike the per-chain node `id`). */
  assetId?: string | null;
  assetCountryName?: string | null;
  companyName?: string | null;
  featuredFacilityProcess?: string | null;
  featuredFacilityProcessName?: string | null;
  productionProcess?: string | null;
  productionProcessName?: string | null;
  nodeId?: string | null;
}

export interface TransactionLineInput {
  quantity?: number | null;
  unit?: string | null;
  secondaryQuantity?: number | null;
  secondaryUnit?: string | null;
  productName?: string | null;
  productCode?: string | null;
  productCategoryId?: string | null;
  productCategoryName?: string | null;
}

export interface TransactionInput {
  linkId: string;
  /** Underlying `tracing_transaction` ids for this edge — the reuse identity. */
  transactionIds?: string[] | null;
  isReceiving?: boolean;
  relatedLinkId?: string | null;
  purchaseOrder?: { id: string; orderNumber?: string | null } | null;
  salesOrder?: { id: string; orderNumber?: string | null } | null;
  supplier?: TransactionEntityInput | null;
  buyer?: TransactionEntityInput | null;
  receiver?: TransactionEntityInput | null;
  documents?: TransactionDocumentInput[] | null;
  lines?: TransactionLineInput[] | null;
}

export interface BuildChunkPayloadsParams {
  transactions: TransactionInput[] | null;
  traceType: string;
  tracingId: string;
  aiSummaryId: string;
  companyId: string;
}

/** One per-chunk unit: the AMQP body to publish plus its reuse key + bookkeeping. */
export interface ChunkPayload {
  chunkId: string;
  /** Real-edge identity (shared across a chain and its sub-chains). Reuse key. */
  transactionKey: string;
  inputHash: string;
  expectedUnits: number;
  payload: Record<string, unknown>;
}

export interface ChunkPayloadsByType {
  classify: ChunkPayload[];
  analyze: ChunkPayload[];
  validate: ChunkPayload[];
}

/**
 * Content identity of a document's file for reuse hashing — the file's
 * `content_md5_hash` (or `file_hash`), NOT the per-chain `document.id`. Two
 * chains referencing the same real transaction see the same file → same id →
 * reuse. Falls back to `document.id` only when no content hash is available,
 * which safely *blocks* cross-chain reuse for that document (never wrong reuse).
 */
const docContentId = (doc: TransactionDocumentInput): string =>
  doc.contentMd5Hash || doc.fileHash || doc.id || '';

/**
 * Order-independent identity of an edge endpoint for reuse hashing: the company
 * (`assetId`) + facility process — stable across chains, unlike the per-chain
 * node `id`/`nodeId`.
 */
const entityContentKey = (entity: TransactionEntityInput | null | undefined) =>
  entity?.assetId
    ? {
        assetId: entity.assetId,
        facilityProcessId: entity.featuredFacilityProcess || '',
      }
    : null;

const mapEntity = (entity: TransactionEntityInput | null | undefined) =>
  entity
    ? {
        id: entity.id || '',
        assetCountryName: entity.assetCountryName || '',
        companyName: entity.companyName || '',
        featuredFacilityProcess: entity.featuredFacilityProcess || '',
        featuredFacilityProcessName: entity.featuredFacilityProcessName || '',
        productionProcess: entity.productionProcess || '',
        productionProcessName: entity.productionProcessName || '',
        facilityProcess: {
          id: entity.featuredFacilityProcess || '',
          name: entity.featuredFacilityProcessName || '',
          productionProcessName: entity.productionProcessName || '',
        },
      }
    : null;

const mapNodeEntity = (entity: TransactionEntityInput | null | undefined) =>
  entity?.id
    ? {
        id: entity.id,
        facilityProcess: {
          id: entity.featuredFacilityProcess || '',
          name: entity.featuredFacilityProcessName || '',
        },
        companyName: entity.companyName || '',
        nodeId: entity.nodeId || '',
      }
    : null;

const mapLine = (line: TransactionLineInput) => ({
  quantity: line.quantity,
  unit: line.unit || '',
  secondaryQuantity: line.secondaryQuantity || 0,
  secondaryUnit: line.secondaryUnit || '',
  productName: line.productName || '',
  productCode: line.productCode || '',
  productCategory: line.productCategoryId || '',
});

/**
 * Split an order's transactions into per-chunk (per-link) payloads for each
 * analysis type. Mirrors the field shaping the all-at-once `prepareAIAnalysisPayloads`
 * used, but emits one message per transaction/node and attaches a stable
 * `inputHash` (the cross-company reuse key).
 */
export const buildChunkPayloads = (
  params: BuildChunkPayloadsParams,
): ChunkPayloadsByType => {
  const { transactions, traceType, tracingId, aiSummaryId, companyId } = params;
  const txns = transactions ?? [];

  const classify: ChunkPayload[] = [];
  const analyze: ChunkPayload[] = [];
  const validate: ChunkPayload[] = [];

  // classify + content: skip receiving transactions (documents merged into main).
  for (const t of txns.filter(tx => !tx.isReceiving)) {
    const docs = t.documents ?? [];
    const lines = t.lines ?? [];
    const transactionKey = computeTransactionKey(
      t.transactionIds ?? [],
      t.linkId,
    );
    const documents = docs.map(doc => ({
      documentId: doc.id,
      documentType: doc.type,
      fileURL: doc.fileUrl ?? null,
      isMandatory: doc.isMandatory || false,
      mimeType: doc.fileContentType || '',
    }));

    // classify chunk — one transaction's documents. Skip txns with no documents.
    if (documents.length > 0) {
      const hashInput = docs.map(doc => ({
        content: docContentId(doc),
        documentType: doc.type,
        isMandatory: doc.isMandatory || false,
        mimeType: doc.fileContentType || '',
      }));
      classify.push({
        chunkId: t.linkId,
        transactionKey,
        inputHash: computeChunkInputHash(TRACING_AI_ANALYSIS_TYPES.CLASSIFY, {
          documents: hashInput,
        }),
        expectedUnits: documents.length,
        payload: {
          aiSummaryId,
          companyId,
          chunkId: t.linkId,
          documents,
          tracingType: traceType,
          tracingId,
        },
      });
    }

    // content chunk — one transaction (always emitted, mirrors prior behaviour).
    const transaction = {
      linkId: t.linkId,
      purchaseOrder: t.purchaseOrder
        ? {
            id: t.purchaseOrder.id,
            orderNumber: t.purchaseOrder.orderNumber || null,
          }
        : null,
      salesOrder: t.salesOrder
        ? { id: t.salesOrder.id, orderNumber: t.salesOrder.orderNumber || null }
        : null,
      supplier: mapEntity(t.supplier),
      buyer: mapEntity(t.buyer),
      receiver: mapEntity(t.receiver),
      documents: docs.map(doc => ({
        id: doc.id,
        fileURL: doc.fileUrl ?? null,
        documentType: doc.type,
        isMandatory: doc.isMandatory || false,
        mimeType: doc.fileContentType || '',
      })),
      lines: lines.map(mapLine),
    };
    analyze.push({
      chunkId: t.linkId,
      transactionKey,
      inputHash: computeChunkInputHash(TRACING_AI_ANALYSIS_TYPES.ANALYZE, {
        documents: docs.map(doc => ({
          content: docContentId(doc),
          documentType: doc.type,
          isMandatory: doc.isMandatory || false,
        })),
        supplier: entityContentKey(t.supplier),
        receiver: entityContentKey(t.receiver),
        buyer: entityContentKey(t.buyer),
        lines: lines.map(mapLine),
      }),
      expectedUnits: transaction.documents.length,
      payload: {
        aiSummaryId,
        chunkId: t.linkId,
        transactions: [transaction],
        tracingType: traceType,
        tracingId,
      },
    });
  }

  // validate: one chunk per node (all transactions, including receiving).
  for (const t of txns) {
    const nodeLines = t.lines ?? [];
    const transactionKey = computeTransactionKey(
      t.transactionIds ?? [],
      t.linkId,
    );
    const node = {
      linkId: t.linkId,
      supplier: {
        id: t.supplier?.id || '',
        facilityProcess: {
          id: t.supplier?.featuredFacilityProcess || '',
          name: t.supplier?.featuredFacilityProcessName || '',
        },
        companyName: t.supplier?.companyName || '',
        nodeId: t.supplier?.nodeId || '',
      },
      relatedLinkId: t.relatedLinkId || null,
      receiver: mapNodeEntity(t.receiver),
      buyer: mapNodeEntity(t.buyer),
      lines:
        nodeLines.length > 0
          ? nodeLines.map(line => ({
              productCategoryId: line.productCategoryId || '',
              productCategoryName: line.productCategoryName || '',
            }))
          : null,
    };
    validate.push({
      chunkId: t.linkId,
      transactionKey,
      inputHash: computeChunkInputHash(TRACING_AI_ANALYSIS_TYPES.VALIDATE, {
        supplier: entityContentKey(t.supplier),
        receiver: entityContentKey(t.receiver),
        buyer: entityContentKey(t.buyer),
        scenario: t.isReceiving ? 'triangle' : 'direct',
        lines:
          nodeLines.length > 0
            ? nodeLines.map(line => line.productCategoryId || '')
            : null,
      }),
      expectedUnits: 1,
      payload: {
        aiSummaryId,
        companyId,
        chunkId: t.linkId,
        nodes: [node],
        tracingType: traceType,
        tracingId,
      },
    });
  }

  return { classify, analyze, validate };
};

export const ANALYSIS_TYPE_KEYS: Record<
  TracingAiAnalysisType,
  keyof ChunkPayloadsByType
> = {
  [TRACING_AI_ANALYSIS_TYPES.CLASSIFY]: 'classify',
  [TRACING_AI_ANALYSIS_TYPES.ANALYZE]: 'analyze',
  [TRACING_AI_ANALYSIS_TYPES.VALIDATE]: 'validate',
};
